import { useState } from 'react';

import {
  Box,
  Checkbox,
  FormikField,
  H2,
  Input,
  RadioGroup,
  Select,
  Separator,
  Shimmer,
  Text,
} from '@betterment/samba-react';
import { useFormikContext } from 'formik';

import { HtmlContent } from '~/b4b/components/html-content/HtmlContent';
import { BenefitReportInputOptionType } from '~/graphql/schema';

import { BenefitReportCreationOption, BenefitReportInputOption } from './data';
import { useReportOptions } from './hooks';
import { ReportFieldsDialog } from './ReportFieldsDialog';

const CheckboxInput = ({ input }: { input: BenefitReportInputOption }) => {
  const { initialValues } = useFormikContext<BenefitReportCreationOption>();
  initialValues[input.attribute] = input.default === 'true';
  return (
    <FormikField name={input.attribute}>
      <FormikField.Control>
        <Checkbox css={{ marginTop: 'space.200' }}>
          <Checkbox.Control id={input.attribute} name={input.attribute} defaultChecked={input.default === 'true'} />
          <Checkbox.Label htmlFor={input.attribute}>{input.label}</Checkbox.Label>
        </Checkbox>
      </FormikField.Control>
    </FormikField>
  );
};

const DateInput = ({ input: { attribute, default: defaultValue, label } }: { input: BenefitReportInputOption }) => {
  const { handleChange, errors, initialValues } = useFormikContext<BenefitReportCreationOption>();
  initialValues[attribute] = defaultValue || '';
  return (
    <FormikField name={attribute} id={attribute}>
      <FormikField.Label>{label}</FormikField.Label>
      <FormikField.Control>
        <Input
          css={{ marginBottom: 0, gap: 'space.50' }}
          id={attribute}
          aria-invalid={!!errors[attribute]}
          aria-errormessage={`${attribute}-error`}
          name={attribute}
          mask="date-mm/dd/yyyy"
          onBlur={handleChange}
        />
      </FormikField.Control>
      <span id={`${attribute}-error`}>
        <FormikField.ErrorMessage />
      </span>
    </FormikField>
  );
};

const YearInput = ({
  input: { attribute, label },
  reportableYears,
}: {
  input: BenefitReportInputOption;
  reportableYears: string[];
}) => {
  const { initialValues } = useFormikContext<BenefitReportCreationOption>();
  const [previousYear] = reportableYears;
  initialValues[attribute] = previousYear;
  return (
    <FormikField name={attribute} id={attribute}>
      <FormikField.Label>{label}</FormikField.Label>
      <FormikField.Control>
        <Select>
          <Select.Trigger aria-label="year">
            <Select.Value placeholder="Select a year" />
          </Select.Trigger>
          <Select.Content>
            {reportableYears.map(year => (
              <Select.Item key={year} value={year}>
                {year}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </FormikField.Control>
      <span id={`${attribute}-error`}>
        <FormikField.ErrorMessage />
      </span>
    </FormikField>
  );
};

const isInputTypeDate: (
  inputType: BenefitReportInputOptionType
) => inputType is typeof BenefitReportInputOptionType.Date = inputType => {
  return inputType === BenefitReportInputOptionType.Date;
};

const isInputTypeBoolean: (
  inputType: BenefitReportInputOptionType
) => inputType is typeof BenefitReportInputOptionType.Boolean = inputType => {
  return inputType === BenefitReportInputOptionType.Boolean;
};

const isInputTypeYear: (
  inputType: BenefitReportInputOptionType
) => inputType is typeof BenefitReportInputOptionType.Year = inputType => {
  return inputType === BenefitReportInputOptionType.Year;
};

const boxCss = {
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  width: '100%',
  marginBottom: 'space.200',
};

const shimmerCss = {
  marginLeft: 'space.200',
  marginTop: 'space.200',
  height: 'size.200',
};

const DEFAULT_LOADING_REPORT_AMOUNT = 20;

const ShimmerLoader = () => {
  return (
    <>
      {[...Array(DEFAULT_LOADING_REPORT_AMOUNT).keys()].map(key => (
        <Box key={key} css={boxCss}>
          <Shimmer
            css={{
              ...shimmerCss,
              width: '30%',
            }}
          />
          <Shimmer
            css={{
              ...shimmerCss,
              width: '90%',
            }}
          />
          {key < DEFAULT_LOADING_REPORT_AMOUNT - 1 && <Separator css={{ marginTop: 'space.200' }} />}
        </Box>
      ))}
    </>
  );
};

interface ReportForFilter {
  name: string;
  description: string;
  columns: string[];
}

const selectReportsMatchingFilter =
  (filter: string) =>
  ({ name, description, columns }: ReportForFilter) => {
    if (!filter.length) {
      return true;
    }

    const caseInsensitiveFilter = filter.toLowerCase();

    return (
      name.toLowerCase().includes(caseInsensitiveFilter) ||
      description.toLowerCase().includes(caseInsensitiveFilter) ||
      columns.some(c => c.toLowerCase().includes(caseInsensitiveFilter))
    );
  };

type KeyboardInputEvent = React.ChangeEvent<HTMLInputElement>;

export const RunReportOptions = () => {
  const { values, resetForm } = useFormikContext<BenefitReportCreationOption>();
  const { reportableYears, options = [], isLoading } = useReportOptions();
  const [filter, setFilter] = useState('');
  const handleFilterChange = ({ target: { value } }: KeyboardInputEvent) => setFilter(value);

  const filteredReportCreationOptions = options.filter(selectReportsMatchingFilter(filter));
  return (
    <>
      <Input
        css={{ marginBottom: 'space.300' }}
        placeholder="Search reports"
        value={filter}
        onChange={handleFilterChange}
      />
      {isLoading && <ShimmerLoader />}
      <RadioGroup
        value={values.type}
        onValueChange={newValue => {
          resetForm({ values: { type: newValue } });
        }}
      >
        {filteredReportCreationOptions.map((report, index) => {
          return (
            <Box key={report.type}>
              <RadioGroup.ItemContainer
                css={{
                  py: 'space.200',
                }}
              >
                <RadioGroup.Item id={report.type} value={report.type} css={{ alignSelf: 'flex-start', mt: '2px' }} />
                <RadioGroup.ItemLabel htmlFor={report.type}>
                  <H2 variant="title100" id={report.altIdentifier} css={{ scrollMarginTop: '100px' }}>
                    {report.name}
                  </H2>
                  <Text as="p">
                    {report.description}{' '}
                    <ReportFieldsDialog
                      reportFields={{
                        name: report.name,
                        description: report.description,
                        columns: report.columns,
                      }}
                    />
                  </Text>

                  {values.type === report.type && (
                    <>
                      {report.inputs.length > 0 && (
                        <Box
                          css={{
                            display: 'inline-flex',
                            flexWrap: 'wrap',
                            width: '100%',
                            pt: 'space.200',
                          }}
                        >
                          {report.inputs?.map(input => (
                            <Box
                              css={{
                                gap: 'space.100',
                                mr: 'space.300',
                                width: isInputTypeBoolean(input.type) ? '90%' : '40%',
                                boxSizing: 'border-box',
                              }}
                              key={`${report.type}-${input.attribute}`}
                            >
                              {isInputTypeDate(input.type) && <DateInput input={input} />}
                              {isInputTypeBoolean(input.type) && <CheckboxInput input={input} />}
                              {isInputTypeYear(input.type) && (
                                <YearInput input={input} reportableYears={reportableYears} />
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {report.additionalGuidance && (
                        <Box css={{ pt: 'space.100' }}>
                          <HtmlContent documentFragment={report.additionalGuidance} />
                        </Box>
                      )}
                    </>
                  )}
                </RadioGroup.ItemLabel>
              </RadioGroup.ItemContainer>
              {index < filteredReportCreationOptions.length - 1 && <Separator />}
            </Box>
          );
        })}
      </RadioGroup>
    </>
  );
};
